import { IExpertCallAction } from '@techspert-io/conferences';
import { IEngagement } from '@techspert-io/engagements';
import { IExpertAvailabilitiesActionTimeSlot } from '@techspert-io/expert-actions';
import { IConferenceReminder } from '../../../features/conferences/models/conference-reminders.models';
import { IResponseFailItem } from '../../../shared/models/api';

export const expertSegmentList: ExpertSegment[] = [
  'Clinical - KOL',
  'Clinical - Prescriber',
  'Academic - KOL',
  'Academic - Researcher',
  'Commercial - C-Suite / executive',
  'Commercial - VP / Director',
  'Commercial - Manager / Senior',
  'Commercial - Entry-level',
  'Payer',
  'Healthcare admin',
  'Nurse',
  'Patient',
  'Caregiver',
];

export const expertSourceList: ExpertSource[] = [
  'cognisearch',
  'historical',
  'deep3',
  'deep-next',
  'pdl-commercial',
  'pdl-enrichment',
  'manualUpload',
  'third-party',
];

export const connectPhaseList: IExpert['connectPhase'][] = [
  'identified',
  'firstFollowUp',
  'secondFollowUp',
  'outreachComplete',
  'outreach',
  'screener',
  'sentToClient',
  'accepted',
  'scheduled',
  'completed',
];

export type ReasonForRejection =
  | 'NULL'
  | 'Wrong geography'
  | 'Irrelevant expertise'
  | 'Scope change'
  | 'Project cancellation'
  | 'Quota reached'
  | 'Client dropped off'
  | 'Client rejected'
  | 'Other'
  | 'Emails bounced'
  | 'Failed screener';

export type ConnectPhase =
  | 'identified'
  | 'firstFollowUp'
  | 'secondFollowUp'
  | 'outreachComplete'
  | 'outreach'
  | 'screener'
  | 'sentToClient'
  | 'accepted'
  | 'scheduled'
  | 'completed';

export type ExpertSegment =
  | 'Clinical - KOL'
  | 'Clinical - Prescriber'
  | 'Academic - KOL'
  | 'Academic - Researcher'
  | 'Commercial - C-Suite / executive'
  | 'Commercial - VP / Director'
  | 'Commercial - Manager / Senior'
  | 'Commercial - Entry-level'
  | 'Payer'
  | 'Healthcare admin'
  | 'Nurse'
  | 'Patient'
  | 'Caregiver';

export type ExpertSource =
  | 'pdl-enrichment'
  | 'manualUpload'
  | 'deep-next'
  | 'deep3'
  | 'pdl-commercial'
  | 'historical'
  | 'deep'
  | 'network'
  | 'third-party'
  | 'cognisearch';

export type ExpertEmailStatus =
  | 'unverified'
  | 'valid'
  | 'pending'
  | 'invalid'
  | 'unknown';

export type ExpertEmailService = 'sendgrid' | 'neverbounce';

export interface IExpertEmail {
  address: string;
  history?: {
    messageStatus: string;
  }[];
  status: ExpertEmailStatus;
  service?: ExpertEmailService;
  isVerified?: boolean;
}

export interface IPhaseTimeStamps {
  phase: string;
  timestamp: number;
}

export interface ISearchExpertData {
  id: string;
  trackingId?: string;
  sourceOpportunityId?: string;
  relevance?: number;
  responsiveness?: number;
}

export type QualtricsDataType =
  | 'array'
  | 'integer'
  | 'number'
  | 'object'
  | 'string';

export interface IExpertScreenerResult {
  responses: {
    id: string;
    question: string;
    answer: string;
    questionType: QualtricsDataType;
  }[];
  dateCompleted: number;
}

export interface IExpertScreenerConfig {
  contactLookupId: string;
  contactId: string;
  distributionId?: string;
  distributionLink?: string;
  dateSent?: number;
  dateCompleted?: number;
}

export interface IExpert {
  affiliations: string[];
  bio: string;
  blocked: boolean;
  callTime: string;
  campaignId?: string;
  clientApproved?: boolean;
  clientNotes?: string;
  conferences: string[];
  connectPhase: ConnectPhase;
  country: string;
  currency: string;
  expertId: string;
  expertise: string[];
  firstName: string;
  firstNameAlias?: string;
  /**
   * @deprecated use 'opportunitySegmentId' instead
   */
  geographicTarget: string;
  lastName: string;
  lastNameAlias?: string;
  lastUpdated?: number;
  linkedInUrl: string;
  onHold?: boolean;
  opportunityId: string;
  opportunitySegmentId?: string;
  ownerConnectId: string;
  phaseTimestamps?: IPhaseTimeStamps[];
  phoneNumbers?: string[];
  portalAffiliations: string[];
  positions?: string[];
  prev_bios?: any;
  prevOpportunities: any;
  /**
   * @deprecated use 'opportunitySegmentId' instead
   */
  profileType: string;
  reasonForRejection: ReasonForRejection;
  recordingAuthorised: boolean;
  requestedAnotherTime?: boolean;
  s_qualifications: string[];
  screenerResponse?: string;
  searchExpertData?: ISearchExpertData;
  searchId: string;
  seenByClient: boolean;
  favourited?: boolean;
  sites: string[];
  source: ExpertSource;
  tagline: string;
  qualifications?: string[];
  unitsPerHour: number;
  costBand?: 'Discounted' | 'Standard' | 'Premium' | 'Premium+';
  expertSourceId?: string;
  expertProfileId?: string;
  expertVisibility?: Record<'clientPortal', boolean>;

  opportunityEmails: string[];
  primaryEmail?: string;
  portalExpertise: string[];
  expertSegment: ExpertSegment;
  rejection: Partial<
    Record<'admin' | 'client', IOpportunityExpertRejectionRecord>
  >;
  timezoneName?: string;
}

export interface IDisplayExpert extends IExpert {
  screenerConfig?: IExpertScreenerConfig;
  screenerResult?: IExpertScreenerResult;
  availabilities?: IExpertAvailabilitiesActionTimeSlot[];
  isSelectable?: boolean;
  isSelected?: boolean;
  engagements?: IEngagement[];
  callActions?: IDisplayExpertCallActions;
}

export interface IDisplayExpertCallActions {
  request: IDisplayExpertCallAction[];
  pending: IDisplayExpertCallAction[];
  complete: IDisplayExpertCallAction[];
}

export interface IDisplayExpertCallAction {
  reminders: IConferenceReminder[];
  callAction: IExpertCallAction;
}

export interface IExpertCreateRequest extends Partial<IExpert> {
  firstName: string;
  lastName: string;
  ownerConnectId: string;
}

export interface IExpertUpdateRequest extends Partial<IExpert> {
  expertId: string;
}

export interface IQueryExpertSearchRequest {
  opportunityId: string;
  searchIds: string[];
  fromPhase?: ConnectPhase;
}

export interface IExtendedExpert extends IExpert {
  opportunityName: string;
  clientId: string;
}

export interface IExpertProp {
  field: string;
  value: IExpert[keyof IExpert];
}

export interface IOpportunityExpertRejectionRecord {
  source: 'unknown';
  code: 'unknown';
  rejectedOn: number;
}

export class ValidationErrors extends Error {
  constructor(public errors: IResponseFailItem<IExpert>[]) {
    super('Validation Failure');

    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class ValidationError extends Error {
  constructor(public error: IResponseFailItem<IExpert>) {
    super('Validation Failure');

    Object.setPrototypeOf(this, new.target.prototype);
  }
}
